import { useLayoutEffect, useRef, useState } from "react";
import { useThrottledFunction } from "./debounce";
import { useHtmlEventListener } from "./event";
import { useHandler } from "./hook";

export function useHover(element: HTMLElement | null): boolean {
  const [hover, setHover] = useState(false);

  useOnHover(element, setHover);

  return hover;
}

export function useTransitioning(
  element: HTMLElement | null,
  property: (value: string) => boolean,
): boolean {
  const [transitioning, setTransitioning] = useState<HTMLElement | null>(
    element,
  );
  useHtmlEventListener(element, "transitionstart", (e) => {
    if (property(e.propertyName)) {
      setTransitioning(e.target as HTMLElement);
    }
  });
  useHtmlEventListener(element, "transitioncancel", (e) => {
    if (property(e.propertyName)) {
      setTransitioning(null);
    }
  });
  useHtmlEventListener(element, "transitionend", (e) => {
    if (property(e.propertyName)) {
      setTransitioning(null);
    }
  });
  useLayoutEffect(() => {
    setTransitioning(null);
  }, [element]);
  return transitioning === element;
}

export function useOnHover(
  element: HTMLElement | null,
  onHover: (value: boolean) => void,
) {
  const ref = useRef<(value: boolean) => void>(onHover);
  ref.current = onHover;

  useHtmlEventListener(element, "mouseenter", () => ref.current(true));
  useHtmlEventListener(element, "mouseleave", () => ref.current(false));

  useLayoutEffect(() => () => ref.current!(false), [element]);
}

/**
 *
 * @deprecated
 *
 * This function is very expensive and should only be used when absolutely necessary.
 * It checks if the mouse is hovering over the given element by comparing the mouse position
 * with the element's bounding rectangle.
 *
 * The current use case is to be able to do a use hover that ignores tooltips.
 */
export function useExpensiveLocationHover(
  element: HTMLElement | null,
): boolean {
  const [hover, setHover] = useState(false);
  const elementRef = useRef<HTMLElement | null>(element);
  elementRef.current = element;

  const regularHover = useHover(element);

  const recalculate = useHandler((e: MouseEvent) => {
    if (!e) {
      return;
    }
    if (!elementRef.current) {
      setHover(false);
      return;
    }

    const rect = elementRef.current.getBoundingClientRect();
    const isInBounds =
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom;

    setHover(isInBounds);
  });

  const throttledRecalculate = useThrottledFunction(recalculate, 100);

  useHtmlEventListener(
    element?.ownerDocument.documentElement || null,
    "mousemove",
    throttledRecalculate,
  );

  useLayoutEffect(() => {
    setHover(false);
  }, [element]);

  return hover || regularHover;
}
